import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import * as lodash from 'lodash'
import { FormFieldComponent } from '../form-field/form-field.component'
import { UntypedFormControl } from '@angular/forms'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { ENTER, COMMA } from '@angular/cdk/keycodes'
import { map, startWith } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { resolveTagLabel } from './form-tags2.utils'

@Component({
  selector: 'app-form-tags2',
  templateUrl: './form-tags2.component.html',
  styleUrls: ['../form-field-common.scss', './form-tags2.component.scss'],
  host: { class: 'form-field-tags2' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormTags2Component extends FormFieldComponent {
  @Input() items: (string | any)[] = [] // available tags
  @Input() bindLabel: string // property name for display label
  @Input() bindValue: string // property name for value
  @Input() searchable = true
  @Input() searchPlaceholder = $localize`Search...`
  @Input() clearable = false
  @Input() addNewTag = true
  @Input() showLabel = true
  @Input() caseSensitive = false
  @Input() placeholder: string = $localize`Type to pick or create tag`
  @Input() createTagFn: (term: string) => any = (term: string) =>
    this.isObjectArray()
      ? {
          [this.bindLabel]: term,
          [this.bindValue]: term,
        }
      : term

  separatorKeysCodes: number[] = [ENTER, COMMA]

  tagCtrl = new UntypedFormControl([])
  filteredTags$: Observable<(string | any)[]>

  @Output() search = new EventEmitter<string>()

  ngOnInit(): void {
    super.ngOnInit()
    this.filteredTags$ = this.tagCtrl.valueChanges.pipe(
      startWith(null),
      map((term) => this.filterTags(term))
    )
  }

  filterTags(term: string | null) {
    term = lodash.trim(term)
    if (!term) return this.items?.slice()
    const reg = new RegExp(lodash.escapeRegExp(term), 'g' + (this.caseSensitive ? '' : 'i'))
    return this.items?.filter((item) => {
      const label = this.getItemLabel(item)
      return label.search(reg) > -1
    })
  }

  add(term: string) {
    term = lodash.trim(term)
    if (!term || !this.addNewTag) {
      return
    }

    const newItem = this.createTagFn(term)
    if (!newItem) {
      return
    }
    this.items.push(newItem)
    const newValue = this.getItemValue(newItem)

    this.ctrl.setValue(
      lodash.uniqBy([...this.ctrl.value, newValue], (value) => {
        return this.caseSensitive ? value : lodash.toLower(value)
      })
    )
    this.tagCtrl.reset()
  }

  removeByValue(value: any) {
    this.ctrl.setValue(
      lodash.filter(this.ctrl.value, (v) => {
        return !this.compareValue(v, value)
      })
    )
  }

  compareValue(v1: string, v2: string) {
    return this.caseSensitive ? v1 === v2 : lodash.toLower(v1) === lodash.toLower(v2)
  }

  isEqual(a: any, b: any) {
    const valueA = this.getItemValue(a)
    const valueB = this.getItemValue(b)
    return this.compareValue(valueA, valueB)
  }

  onSelect(event: MatAutocompleteSelectedEvent) {
    const item = event.option.value
    const value = this.getItemValue(item)
    this.ctrl.setValue(
      lodash.uniqBy([...this.ctrl.value, value], (v) => {
        return this.caseSensitive ? v : lodash.toLower(v)
      })
    )
    this.tagCtrl.reset()
  }

  onSelectDropdownOpen() {
    this.focused = true
  }

  onSelectDropdownClose() {
    this.focused = false
  }

  getItemLabel = (item: any): string => {
    if (this.isObjectArray()) {
      return this.bindLabel ? String(lodash.get(item, this.bindLabel)) : String(item)
    }
    return String(item)
  }

  getItemValue = (item: any): string => {
    if (this.isObjectArray()) {
      return this.bindValue ? String(lodash.get(item, this.bindValue)) : String(item)
    }
    return String(item)
  }

  private isObjectArray(): boolean {
    return !!this.bindValue || !!this.bindLabel
  }

  getItemByValue = (value: string): any => {
    return this.items.find((item) => this.compareValue(this.getItemValue(item), String(value)))
  }

  getItemLabelByValue = (value: string): string => {
    return resolveTagLabel(this.items, value, this.getItemValue, this.getItemLabel, this.compareValue.bind(this))
  }
}
